import React, { useCallback, useContext } from 'react';

import {
  ChipField,
  Form,
  Labeled,
  ReferenceArrayField,
  ReferenceArrayInput,
  SelectArrayInput,
  SingleFieldList,
  useNotify,
  usePermissions,
  useRefresh,
  useUpdate,
} from 'react-admin';

import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import CreateIcon from '@mui/icons-material/Create';

import { EntityContext } from '../../customFields/ReferenceEntityField';

import actions, { Actions } from '../../constants/actions';
import errorNotification from '../../utilities/errorNotification';
import hasAccess from '../../utilities/hasAccess';
import useDialogStatus from '../../hooks/useDialogStatus';
import entityTypes, { KnownEntityTypes, resourceFromEntityTypes } from '../../constants/entityTypes';

const entityTypeUpdatePermissionMap: Record<KnownEntityTypes, Actions> = {
  [entityTypes.ACCOUNT]: actions.ACCOUNT_ENTITY_UPDATE,
  [entityTypes.BUSINESS]: actions.BUSINESS_UPDATE,
  [entityTypes.CAR]: actions.CAR_UPDATE,
  [entityTypes.INDIVIDUAL]: actions.INDIVIDUAL_UPDATE,
  [entityTypes.PRODUCT]: actions.PRODUCT_UPDATE,
};

const GroupsDialogEditField = () => {
  const { permissions } = usePermissions<Actions[]>();
  const { open, openDialog, closeDialog } = useDialogStatus();
  const notify = useNotify();
  const refresh = useRefresh();
  const entityContextValue = useContext(EntityContext);
  const [updateOne] = useUpdate();
  const entityType = (entityContextValue?.type ?? entityTypes.BUSINESS) as KnownEntityTypes;
  const resource = resourceFromEntityTypes[entityType];
  const handleSubmit = useCallback((data: { groups?: string[] }) => {
    updateOne(
      resource,
      {
        id: entityContextValue?.id,
        data: { groups: data.groups },
      },
      {
        onSuccess: () => {
          notify('Groups updates');
          refresh();
          closeDialog();
        },
        onError: (error) => {
          notify(errorNotification(error, 'Groups could not be updated'), { type: 'error' });
          refresh();
        },
      },
    );
  }, [closeDialog, entityContextValue?.id, notify, refresh, resource, updateOne]);

  if (!entityContextValue) return null;

  const { type } = entityContextValue;

  const showEdit = hasAccess(permissions, entityTypeUpdatePermissionMap[type as KnownEntityTypes])
  && hasAccess(permissions, actions.GROUP_LIST);

  if (!hasAccess(permissions, actions.GROUP_GET)) return null;

  return (
    <>
      <Dialog open={open} onClose={closeDialog}>
        <Form onSubmit={handleSubmit}>
          <DialogTitle fontWeight="bold">
            Update groups
          </DialogTitle>
          <DialogContent>
            <ReferenceArrayInput
              page={1}
              perPage={200}
              label="Groups"
              source="groups"
              reference="groups"
              filter={{ active: true }}
            >
              <SelectArrayInput optionText="name" />
            </ReferenceArrayInput>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>
              Cancel
            </Button>
            <Button variant="contained" type="submit">
              Save
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
      <Labeled label="Groups">
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <ReferenceArrayField
            page={1}
            perPage={200}
            reference="groups"
            source="groups"
            filter={{ active: true }}
          >
            <SingleFieldList empty={<Typography>None</Typography>}>
              <ChipField source="name" size="small" />
            </SingleFieldList>
          </ReferenceArrayField>
          {showEdit && (
            <Button startIcon={<CreateIcon color="secondary" />} size="small" onClick={openDialog}>
              Edit
            </Button>
          )}
        </Box>
      </Labeled>
    </>
  );
};

export default GroupsDialogEditField;
